// Join 画面の背景アニメーション。canvas 上で粒子フロー + 近接リンク + ポインタ
// リップルを描く。元実装 (Downloads/wincast_connect_mock_uniform_respawn.html)
// を TypeScript に移植し、CLAUDE.md の規約 (no any / explicit return type) に
// 合わせて整理。join-view が hidden の間は getBoundingClientRect が 0 を返すので
// 自動的にスキップしループ自体は空回しのまま resource を消費しない。

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  age: number;
  size: number;
  green: boolean;
  heat: number;
};

type Ripple = { x: number; y: number; r: number; a: number };

const PARTICLE_COUNT = 150;
const SPEED = 0.3;
const LINK_ALPHA = 0.4;
const MAX_LINK_DIST = 70;
const MAX_LINK_DIST_SQ = MAX_LINK_DIST * MAX_LINK_DIST;
// 中央付近 (フォーム周辺) ほど粒子・リンク濃度を高く保つための「外側で薄くなる」係数。
const SAFE_RX = 200;
const SAFE_RY = 120;
const LINK_RX = 210;
const LINK_RY = 130;

function makeParticle(): Particle {
  return { x: 0, y: 0, vx: 0, vy: 0, life: 0, age: 0, size: 0, green: false, heat: 0 };
}

function spawn(p: Particle, w: number, h: number, initial: boolean): void {
  p.x = Math.random() * w;
  p.y = Math.random() * h;
  p.vx = 0;
  p.vy = 0;
  p.life = 6 + Math.random() * 10;
  // initial=true 時のみ age をランダム化することで、起動直後にも分散した寿命を持つ。
  p.age = initial ? Math.random() * p.life : 0;
  p.size = 0.7 + Math.random() * 1.3;
  // 12% を green に。WinCast の補色アクセント。
  p.green = Math.random() < 0.12;
  p.heat = 0;
}

function field(x: number, y: number, t: number): number {
  const a = Math.sin(x * 0.0042 + t * 0.18) + Math.cos(y * 0.0051 - t * 0.13);
  const b = Math.sin((x + y) * 0.0023 + t * 0.07);
  return a * 1.4 + b * 2.2;
}

// (x,y) が楕円中心 (cx,cy) からどれくらい外側にあるかを 0..1 で返す。
// 外側ほど 1 に近づき、フェード重みとして使う。
function safety(x: number, y: number, cx: number, cy: number, rx: number, ry: number): number {
  const ex = (x - cx) / rx;
  const ey = (y - cy) / ry;
  const s = Math.min(1, ex * ex + ey * ey);
  return s * s;
}

export function initJoinBg(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let w = 0;
  let h = 0;

  const resize = (): void => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    w = rect.width;
    h = rect.height;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#080b10";
    ctx.fillRect(0, 0, w, h);
  };
  resize();
  window.addEventListener("resize", resize);

  const particles: Particle[] = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const p = makeParticle();
    spawn(p, w || 1, h || 1, true);
    particles.push(p);
  }

  const ripples: Ripple[] = [];

  canvas.addEventListener("pointerdown", (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ripples.push({ x, y, r: 6, a: 0.55 });
    // 半径 160 以内の粒子に外向き推力 + 熱量を加える。
    for (const p of particles) {
      const dx = p.x - x;
      const dy = p.y - y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      if (d < 160) {
        const imp = (1 - d / 160) * 4.5;
        p.vx += (dx / d) * imp;
        p.vy += (dy / d) * imp;
        p.heat = Math.min(1, p.heat + (1 - d / 160));
      }
    }
  });

  let t = 0;
  const frame = (): void => {
    // 画面遷移で #join-view が display:none になっている間は計算をスキップ。
    // rAF コール自体は維持して join-view 再表示時に即再開できるようにする。
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      requestAnimationFrame(frame);
      return;
    }
    // ウィンドウサイズが変わった (CSS リサイズ) ら canvas の内部解像度を追従させる。
    if (Math.abs(rect.width - w) > 0.5 || Math.abs(rect.height - h) > 0.5) {
      resize();
    }

    t += 0.016;
    // 毎フレーム不透明な背景色で完全クリア。半透明フェードで残像を出すと、線・粒子の
    // 低 α 描画が長時間ピクセルに残り続けて多角形状の累積アーティファクトになるため、
    // トレイル方式は採らず素直にクリアする。
    ctx.fillStyle = "#080b10";
    ctx.fillRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2 + 10;

    for (const p of particles) {
      const ang = field(p.x, p.y, t);
      const sp = SPEED * (0.7 + p.size * 0.4);
      p.vx += (Math.cos(ang) * sp - p.vx) * 0.05;
      p.vy += (Math.sin(ang) * sp * 0.6 - p.vy) * 0.05;
      p.vx *= 0.965;
      p.vy *= 0.965;
      p.x += p.vx;
      p.y += p.vy;
      p.age += 0.016;
      p.heat *= 0.96;

      if (p.x < -20 || p.x > w + 20 || p.y < -20 || p.y > h + 20 || p.age > p.life) {
        spawn(p, w, h, false);
        continue;
      }

      const safe = safety(p.x, p.y, cx, cy, SAFE_RX, SAFE_RY);
      const fade = Math.min(1, p.age * 1.5, (p.life - p.age) * 1.5);
      const alpha = (0.5 + p.heat * 0.5) * fade * (0.58 + 0.42 * safe);
      const sz = p.size * (1 + p.heat * 0.8);

      ctx.fillStyle = p.green
        ? `rgba(63,185,80,${alpha.toFixed(3)})`
        : `rgba(${Math.round(88 + p.heat * 80)},${Math.round(166 + p.heat * 50)},255,${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, sz, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.lineWidth = 0.6;
    for (let i = 0; i < particles.length; i++) {
      const a1 = particles[i];
      if (!a1) continue;
      for (let j = i + 1; j < particles.length; j++) {
        const b1 = particles[j];
        if (!b1) continue;
        const dx = a1.x - b1.x;
        const dy = a1.y - b1.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > MAX_LINK_DIST_SQ) continue;
        const safe = safety((a1.x + b1.x) / 2, (a1.y + b1.y) / 2, cx, cy, LINK_RX, LINK_RY);
        const heat = Math.max(a1.heat, b1.heat);
        const la =
          (1 - d2 / MAX_LINK_DIST_SQ) *
          0.16 *
          LINK_ALPHA *
          (0.45 + 0.55 * safe) *
          (1 + heat * 1.2);
        if (la < 0.008) continue;
        ctx.strokeStyle = `rgba(88,166,255,${Math.min(0.4, la).toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(a1.x, a1.y);
        ctx.lineTo(b1.x, b1.y);
        ctx.stroke();
      }
    }

    for (let i = ripples.length - 1; i >= 0; i--) {
      const rp = ripples[i];
      if (!rp) continue;
      rp.r += 3.4;
      rp.a *= 0.945;
      if (rp.a < 0.01) {
        ripples.splice(i, 1);
        continue;
      }
      ctx.strokeStyle = `rgba(120,180,255,${rp.a.toFixed(3)})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(rp.x, rp.y, rp.r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = `rgba(120,180,255,${(rp.a * 0.35).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(rp.x, rp.y, rp.r * 0.7, 0, Math.PI * 2);
      ctx.stroke();
    }

    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}
